<template>
	<Card
		:image="article.image"
		:image-text="article.title"
		:flags="article.flags"
	>
		<template slot="header">
			<transition name="slide-x-transition">
				<slot name="checkbox"/>
			</transition>
			<div class="body-1 my-1">
				<span class="body-2 secondary--text">{{ article.userName }}</span>
				added
				<span class="secondary--text">{{ article.timeAddedAgo }}</span>
			</div>
			<v-spacer/>
			<v-tooltip
				v-if="article.errorsSum > 0"
				top
			>
				<v-btn
					slot="activator"
					flat
					class="ma-0"
					color="info"
					icon
				>
					<v-icon>info</v-icon>
				</v-btn>
				<div
					v-for="(error, index) in article.errorsList"
					:key="index"
				>
					{{ error }}
				</div>
			</v-tooltip>
			<ArticleImportance
				v-if="!isArchive"
				:article="article"
			/>
		</template>
		<template slot="title">
			<div class="sub-title">
				<span
					:title="article.author"
					class="truncate"
				>
					{{ article.author }}
				</span>
				<span v-if="article.author && article.date"> / </span>{{ article.date }}
				<span v-if="(article.author || article.date) && category"> / </span>
				<span title="category">{{ category }}</span>
			</div>
			<div class="title full-width">
				<span
					:title="article.title"
					class="truncate"
				>
					{{ article.title }}
				</span>
			</div>
		</template>
		<template slot="additional">
			<v-container
				px-1
				pb-2
				pt-0
			>
				<v-layout
					row
					wrap
					:class="{'text-xs-center': this.$store.get('isSkilled', 'twitter')}"
					px-2
				>
					<v-flex xs6>
						<v-chip
							label
							class="v-chip__facebook"
							text-color="white"
						>
							<v-icon left>fa fa-facebook</v-icon>{{ article.facebookShares | formatNumber }}
						</v-chip>
					</v-flex>
					<v-flex v-if="this.$store.get('isSkilled', 'twitter')" xs6>
						<v-chip
							label
							class="v-chip__twitter"
							text-color="white"
						>
							<v-icon left>fa fa-twitter</v-icon>{{ article.twitterShares }}
						</v-chip>
					</v-flex>
				</v-layout>
			</v-container>
			<v-divider/>
			<v-tooltip
				v-if="article.publication"
				top
			>
				<div
					slot="activator"
					class="publication mx-3"
				>
					<img
						:src="article.publication.logo"
						class="publication-img"
						@error="imgError"
					>
					<p class="body-2">{{ article.publicationName }}</p>
				</div>
				<div v-if="article.publication">
					<div class="body-2">{{ article.publicationName }}</div>
					<div>Reach (users): {{ article.publication.alexaStats.reachUsers | shortNumber }}</div>
				</div>
			</v-tooltip>
			<div
				v-else
				class="publication mx-3"
			>
				<img
					:src="pubNoLogo"
					class="publication-img"
					@error="imgError"
				>
				<p class="body-2">{{ article.publicationName }}</p>
			</div>
		</template>
		<template slot="actions">
			<v-btn
				v-if="!isArchive && $userCan('manage_reports') && !report.isArchivedOrParentArchived"
				:disabled="article.changing"
				slot="activator"
				ripple
				flat
				@click.native.stop="$emit('edit', article)"
			>
				Edit
			</v-btn>
			<v-btn
				v-if="isArchive && $userCan('manage_reports') && !report.isArchivedOrParentArchived"
				:disabled="article.changing"
				slot="activator"
				ripple
				flat
				color="success"
				@click="$emit('restore', article)"
			>
				Restore
			</v-btn>
			<v-btn
				:href="article.url"
				flat
				ripple
				target="_blank"
			>
				View
				<v-icon
					small
					class="pl-2"
				>
					fa fa-external-link
				</v-icon>
			</v-btn>
			<v-spacer/>
			<v-menu
				v-if="!isArchive"
				v-model="additionalMenu"
				top
				left
			>
				<v-btn
					v-if="$userCan('manage_reports') && !report.isArchivedOrParentArchived"
					slot="activator"
					:disabled="article.changing"
					small
					icon
				>
					<v-icon>more_vert</v-icon>
				</v-btn>
				<v-list v-if="additionalMenu">
					<v-list-tile @click="article.updateSocialStats()">
						<v-list-tile-title>Update Social Stats</v-list-tile-title>
					</v-list-tile>
					<v-list-tile @click="$emit('set-add-reprints', article)">
						<v-list-tile-title>Add Reprints</v-list-tile-title>
					</v-list-tile>
					<v-list-tile @click="$emit('set-image', article)">
						<v-list-tile-title>Set Image</v-list-tile-title>
					</v-list-tile>
					<v-list-tile
						v-if="!hideImageSettings"
						@click="$emit('set-report-image', article.image)"
					>
						<v-list-tile-title>Set as Report Image</v-list-tile-title>
					</v-list-tile>
					<v-list-tile
						v-if="$userCan('manage_projects') && !hideImageSettings"
						@click="$emit('set-project-image', article.image)"
					>
						<v-list-tile-title>Set as Project Image</v-list-tile-title>
					</v-list-tile>
					<v-list-tile @click="$store.set('articlesBulkDialog/openArticleBulkDialog!', { action: 'move', articles: [article] })">
						<v-list-tile-title>Move Article</v-list-tile-title>
					</v-list-tile>
					<v-list-tile @click="$store.set('articlesBulkDialog/openArticleBulkDialog!', { action: 'copy', articles: [article] })">
						<v-list-tile-title>Copy Article</v-list-tile-title>
					</v-list-tile>
					<v-list-tile @click="$store.set('articlesDeleteDialog/openArticleDeleteDialog!', [article])">
						<v-list-tile-title class="error--text">Delete Article</v-list-tile-title>
					</v-list-tile>
				</v-list>
			</v-menu>
		</template>
	</Card>
</template>

<script>
import { Publication } from '@/models/Publication';
import Card from '@/components/Card';
import ArticleImportance from '@/components/Article/ArticleImportance';

export default {
	components: {
		Card,
		ArticleImportance,
	},
	props: {
		article: { type: Object, required: true },
		hideImageSettings: { type: Boolean, default: false },
		isArchive: { type: Boolean, default: false },
	},
	data() {
		return {
			pubNoLogo: Publication.noLogo,
			pubLogoBaseURL: 'https://logo.clearbit.com/',
			additionalMenu: false,
		};
	},
	computed: {
		report() {
			return this.$store.get('reports/currentReport');
		},
		categories() {
			return this.$store.get('categories');
		},
		category() {
			const id = this.article.categoryId;
			const category = this.categories.find(cat => cat.id === id);
			if (category) {
				return category.name;
			}
			return null;
		},
	},
	methods: {
		imgError() {
			// fail silently else vuetify console logs error
		},
	},
};
</script>

<style lang="scss" scoped>
$grey: #BDBDBD;

.publication {
	display: flex;
	align-items: center;
	p {
		margin: 0;
		padding: 0 1rem;
	}
}
.publication-img {
	height: 32px;
	min-width: 32px;
	&:before {
		content: ' ';
		display: block;
		position: absolute;
		height: 32px;
		width: 32px;
		background-color: $grey;
	}
}
.sub-title, .title{
	max-width: 100%;
	.truncate {
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		vertical-align: bottom;
		display: inline-block;
	}
}
.full-width {
	width: 100%;
	.truncate {
		line-height: 105%;
	}
}
.v-chip__twitter {
	background-color: #1da1f2;
}
.v-chip__facebook {
	background-color: #3b5998;
}
</style>
